import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, Envelope, ExclamationTriangle, Link45deg } from 'react-bootstrap-icons';
import { useLocale } from '../i18n/LocaleContext';
import useSeo from '../components/useSeo';
import { useAuth } from '../auth/AuthContext';
import Wordmark from '../components/Wordmark';
import { loginPathFor } from '../config/returnTo';
import { PROBLEMS, SUPPORT_EMAIL, problemFor } from './embedProblems';
import './EmbeddingProblems.scss';

// partners.outfino.app/embedding-problems — public (no sign-in), the portal's
// branding, HU/EN by the browser with a switch. See embedProblems.js.

export default function EmbeddingProblems() {
  const { t, lang, setLang } = useLocale();
  const { user } = useAuth();
  const [target] = useState(() => {
    if (typeof window === 'undefined') return null;
    return problemFor(new URLSearchParams(window.location.search).get('code')) || problemFor(window.location.hash);
  });
  const [open, setOpen] = useState(() => new Set(target ? [target] : []));
  const refs = useRef({});

  useSeo({
    title: `${t('problems.title')} · Outfino Partners`,
    description: t('problems.lead'),
    path: '/embedding-problems',
    lang,
  });

  // The deep link: scroll to the entry (opened and highlighted above).
  useEffect(() => {
    if (!target) return undefined;
    const id = setTimeout(() => refs.current[target]?.scrollIntoView?.({ block: 'start', behavior: 'smooth' }), 60);
    return () => clearTimeout(id);
  }, [target]);

  function toggle(code, isOpen) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (isOpen) next.add(code);
      else next.delete(code);
      return next;
    });
  }

  // Into the portal: straight there when signed in, else via the login.
  const portal = (to) => (user ? to : loginPathFor(to));

  function stepLink(link) {
    if (!link) return null;
    if (link.href) {
      return (
        <a className="ep-link" href={`${link.href}?subject=${encodeURIComponent(t('problems.mailSubject'))}`}>
          <Envelope size={13} aria-hidden="true" />
          {t(link.label, { email: SUPPORT_EMAIL })}
        </a>
      );
    }
    return (
      <Link className="ep-link" to={portal(link.to)}>
        {t(link.label)}
        <ArrowRight size={13} aria-hidden="true" />
      </Link>
    );
  }

  return (
    <div className="ep" data-testid="embedding-problems">
      <header className="ep-top">
        <div className="ep-top__inner">
          <Link to="/" className="ep-top__brand" aria-label="Outfino Partners">
            <Wordmark size={30} />
          </Link>
          <div className="ep-top__actions">
            <div className="ep-lang" role="group" aria-label={t('problems.language')}>
              {['hu', 'en'].map((l) => (
                <button
                  key={l}
                  type="button"
                  className={`ep-lang__btn${lang === l ? ' is-active' : ''}`}
                  aria-pressed={lang === l}
                  onClick={() => setLang(l)}
                  data-testid={`ep-lang-${l}`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <Link to={user ? '/dashboard' : '/login'} className="btn-o btn-o--dark btn-o--sm">
              {t(user ? 'problems.openPortal' : 'nav.login')}
            </Link>
          </div>
        </div>
      </header>

      <main className="ep-main">
        <p className="ep-eyebrow">{t('problems.eyebrow')}</p>
        <h1 className="ep-title">{t('problems.title')}</h1>
        <p className="ep-lead">{t('problems.lead')}</p>

        <nav className="ep-index" aria-label={t('problems.index')}>
          {PROBLEMS.map((p) => (
            <a key={p.code} href={`?code=${p.code}`} className={`ep-index__item${target === p.code ? ' is-target' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                toggle(p.code, true);
                window.history.replaceState(null, '', `?code=${p.code}`);
                refs.current[p.code]?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
              }}
            >
              {t(`problems.${p.code}.short`)}
            </a>
          ))}
        </nav>

        <div className="ep-list">
          {PROBLEMS.map((p) => {
            const isOpen = open.has(p.code);
            const links = [p.link, p.link2].filter(Boolean);
            return (
              <details
                key={p.code}
                id={p.code}
                ref={(el) => { refs.current[p.code] = el; }}
                className={`ep-item${target === p.code ? ' is-target' : ''}`}
                open={isOpen}
                onToggle={(e) => {
                  if (e.currentTarget.open !== isOpen) toggle(p.code, e.currentTarget.open);
                }}
                data-testid={`problem-${p.code}`}
                data-code={p.code}
              >
                <summary className="ep-item__head">
                  <span className="ep-item__icon"><ExclamationTriangle size={15} aria-hidden="true" /></span>
                  <span className="ep-item__titles">
                    <span className="ep-item__where">
                      {p.where ? t(`problems.where.${p.where}`) : t('problems.where.symptom')}
                      <code className="ep-item__code">{p.code}</code>
                    </span>
                    <span className="ep-item__msg">
                      {p.where === 'frame' || p.where === 'portal' ? `„${t(`problems.${p.code}.msg`)}”` : t(`problems.${p.code}.msg`)}
                    </span>
                  </span>
                  <ChevronDown size={15} className="ep-item__chev" aria-hidden="true" />
                </summary>
                <div className="ep-item__body">
                  <p className="ep-item__means">{t(`problems.${p.code}.means`)}</p>
                  {p.causes && <p className="ep-item__causes">{t(`problems.${p.code}.causes`)}</p>}
                  <ol className="ep-steps">
                    {Array.from({ length: p.steps }, (_, i) => i + 1).map((n) => (
                      <li key={n}>
                        <span>{t(`problems.${p.code}.step${n}`, { email: SUPPORT_EMAIL })}</span>
                        {links.filter((l) => l.step === n).map((l) => <span key={l.label}> {stepLink(l)}</span>)}
                      </li>
                    ))}
                  </ol>
                  <a className="ep-item__anchor" href={`?code=${p.code}`} aria-label={t('problems.permalink')} title={t('problems.permalink')}>
                    <Link45deg size={14} aria-hidden="true" />
                    {t('problems.permalink')}
                  </a>
                </div>
              </details>
            );
          })}
        </div>

        <p className="ep-foot">
          {t('problems.contact')}{' '}
          <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(t('problems.mailSubject'))}`}>{SUPPORT_EMAIL}</a>
        </p>
      </main>
    </div>
  );
}
